import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import Reveal from '../../components/Reveal/Reveal';
import ArrowIcon from '../../components/icons/ArrowIcon';
import { getOrganizationSchema, getLocalBusinessSchema } from '../../utils/structuredData';
import {
  IconUsers, IconTarget, IconScale, IconShield,
  IconEnterprise, IconWeb, IconMobile, IconCloud,
} from '../../components/icons/ServiceIcons';
import './About.css';

const PUBLIC_URL = process.env.PUBLIC_URL || '';
const HERO_BG = { '--hero-bg': `url(${PUBLIC_URL}/assets/hero-banner.png)` };
const CTA_BG = { '--cta-bg': `url(${PUBLIC_URL}/assets/cta-banner.png)` };

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const HIGHLIGHTS = [
  { label: 'Founded', value: '2025' },
  { label: 'Based in', value: 'Jaipur, India' },
  { label: 'Focus', value: 'Custom software' },
  { label: 'Approach', value: 'Clear delivery' },
];

const MISSION_ITEMS = [
  {
    title: 'Our Mission',
    text: 'Help businesses turn ideas into working software with clear scope, direct communication, and practical delivery — without unnecessary complexity.',
  },
  {
    title: 'Our Vision',
    text: 'Become a trusted software partner for growing companies that want reliable products, maintainable code, and a team that stays easy to work with.',
  },
  {
    title: 'Our Values',
    text: 'Clarity over jargon. Ownership over hand-offs. Honest timelines. Secure defaults. And software you can extend after launch.',
  },
];

const STORY = [
  {
    year: '2025',
    title: 'CodeInventiv Solutions founded',
    text: 'Started in Jaipur with a simple goal: build custom software, web apps, and mobile products with clear communication and realistic delivery.',
  },
  {
    year: 'Now',
    title: 'Building with businesses',
    text: 'We work with startups and growing teams on practical products — from internal tools and portals to customer apps and cloud setup.',
  },
  {
    year: 'Next',
    title: 'Growing through real projects',
    text: 'We are expanding our portfolio with authentic case studies and continuing to refine how we scope, build, and support software.',
  },
];

const PRINCIPLES = [
  {
    icon: <IconTarget />,
    title: 'Clear ownership',
    text: 'You work with people close to the product — not a long chain of hand-offs.',
  },
  {
    icon: <IconUsers />,
    title: 'Direct communication',
    text: 'Regular updates, honest status, and questions answered without fluff.',
  },
  {
    icon: <IconShield />,
    title: 'Responsibility',
    text: 'We take ownership of scope, quality, and delivery decisions we make with you.',
  },
  {
    icon: <IconScale />,
    title: 'Adaptive partnership',
    text: 'Fixed-scope projects, dedicated teams, or team extension — based on your stage.',
  },
];

const WHAT_WE_DO = [
  {
    icon: <IconEnterprise size={28} />,
    title: 'Custom software',
    text: 'Business apps, portals, and automation shaped around your workflows.',
  },
  {
    icon: <IconWeb size={28} />,
    title: 'Web applications',
    text: 'Secure, maintainable web apps and customer-facing portals.',
  },
  {
    icon: <IconMobile size={28} />,
    title: 'Mobile apps',
    text: 'iOS and Android products for customers or field teams.',
  },
  {
    icon: <IconCloud size={28} />,
    title: 'Cloud & support',
    text: 'Deployments, CI/CD, and optional support after launch.',
  },
];

const About = () => {
  const [openMission, setOpenMission] = useState(0);
  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://www.codeinventivsolutions.com';

  return (
    <div className="about-page">
      <SEO
        title="About Us | CodeInventiv Solutions — Software Development Company in Jaipur"
        description="CodeInventiv Solutions is a software development company founded in 2025 in Jaipur, India. We build custom software, web applications, and mobile apps with clear delivery."
        keywords="about CodeInventiv Solutions, software development company Jaipur, custom software company India, web and mobile app development team"
        structuredData={[
          getOrganizationSchema(siteUrl),
          getLocalBusinessSchema(siteUrl, 'india'),
        ]}
        ogType="website"
      />

      <section className="page-hero" style={HERO_BG}>
        <div className="container page-hero-inner">
          <Reveal>
            <div className="section-eyebrow on-dark">About Us</div>
            <p className="page-hero-locale">Based in Jaipur, India</p>
            <h1 className="page-hero-title">
              Turning ideas into software<br />
              <em>that businesses can rely on</em>
            </h1>
            <p className="page-hero-sub">
              CodeInventiv Solutions helps growing companies plan, build, and support custom
              software, web applications, and mobile apps — with clear scope and practical delivery.
            </p>
            <div className="page-hero-points">
              <span><CheckIcon /> Built by an experienced engineering team</span>
              <span><CheckIcon /> Founded in 2025</span>
              <span><CheckIcon /> Jaipur, India</span>
            </div>
            <div className="page-hero-actions">
              <Link to="/contact" className="btn btn-gold">
                Get a Quote
                <ArrowIcon />
              </Link>
              <Link to="/services" className="btn btn-ghost-light">Our Services</Link>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section section-light" id="who-we-are">
        <div className="container about-intro-grid">
          <Reveal className="about-intro-copy">
            <div className="section-eyebrow">Who We Are</div>
            <h2 className="section-title">A focused software partner for <em>clarity-first delivery</em></h2>
            <p className="section-sub">
              We are a software development company founded in 2025 in Jaipur. Our work centers on
              custom business software, web apps, mobile products, cloud setup, and integrations —
              delivered with direct communication and realistic timelines.
            </p>
            <p className="section-sub">
              We keep engagement simple: agree what will be built, share progress regularly, and
              hand over source code and documentation you can maintain after launch.
            </p>
            <ul className="about-check-list">
              <li><CheckIcon /> Custom software and internal tools</li>
              <li><CheckIcon /> Web and mobile application development</li>
              <li><CheckIcon /> Cloud, DevOps, and post-launch support</li>
              <li><CheckIcon /> API integration and automation</li>
            </ul>
          </Reveal>
          <Reveal className="about-intro-media">
            <img
              src="/assets/team-banner.png"
              alt="Software development work at CodeInventiv Solutions"
              loading="lazy"
            />
            <div className="about-x-chip">
              <span className="about-x-chip-num">2025</span>
              <span className="about-x-chip-lbl">Founded<br />in Jaipur</span>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="section-dark about-highlights-band">
        <div className="container about-highlights">
          {HIGHLIGHTS.map((item) => (
            <Reveal key={item.label} className="about-highlight">
              <span className="about-highlight-value">{item.value}</span>
              <span className="about-highlight-label">{item.label}</span>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="section section-pale" id="mission">
        <div className="container about-mission-wrap">
          <Reveal className="section-head">
            <div className="section-eyebrow">Purpose</div>
            <h2 className="section-title">Driven by clarity and <em>thoughtful delivery</em></h2>
            <p className="section-sub">
              We build reliable software aligned to business goals — and we treat communication as
              part of the product, not an afterthought.
            </p>
          </Reveal>
          <div className="about-mission-list">
            {MISSION_ITEMS.map((item, i) => {
              const open = openMission === i;
              return (
                <Reveal key={item.title} className={`about-mission-item${open ? ' open' : ''}`}>
                  <button
                    type="button"
                    className="about-mission-toggle"
                    aria-expanded={open}
                    onClick={() => setOpenMission(open ? -1 : i)}
                  >
                    <span className="about-mission-num">{String(i + 1).padStart(2, '0')}</span>
                    <span className="about-mission-title">{item.title}</span>
                    <span aria-hidden="true">{open ? '−' : '+'}</span>
                  </button>
                  {open && <p className="about-mission-text">{item.text}</p>}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section section-light" id="story">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">Our Story</div>
            <h2 className="section-title">How CodeInventiv <em>got started</em></h2>
            <p className="section-sub">
              A short, honest timeline — we are a young company building carefully, project by project.
            </p>
          </Reveal>
          <div className="about-story-list">
            {STORY.map((item) => (
              <Reveal key={item.title} as="article" className="about-story-item">
                <div className="about-story-year">{item.year}</div>
                <div>
                  <h3>{item.title}</h3>
                  <p>{item.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-pale" id="principles">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">How We Work</div>
            <h2 className="section-title">Principles that shape <em>every engagement</em></h2>
          </Reveal>
          <div className="about-principles-grid">
            {PRINCIPLES.map((item) => (
              <Reveal key={item.title} as="article" className="about-principle-card">
                <div className="about-principle-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="section section-light" id="what-we-do">
        <div className="container">
          <Reveal className="section-head">
            <div className="section-eyebrow">What We Do</div>
            <h2 className="section-title">Software services for <em>growing businesses</em></h2>
            <p className="section-sub">
              Explore the full list on our services page — here is a quick overview of where we focus.
            </p>
          </Reveal>
          <div className="about-services-grid">
            {WHAT_WE_DO.map((item) => (
              <Reveal key={item.title} as="article" className="about-service-card">
                <div className="about-service-icon">{item.icon}</div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </div>
          <Reveal className="about-services-cta">
            <Link to="/services" className="btn btn-gold">
              View All Services
              <ArrowIcon />
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="cta-band" style={CTA_BG}>
        <div className="container cta-inner">
          <Reveal className="cta-copy">
            <h2>Let’s talk about your next <em>software project</em></h2>
            <p>
              Share a short brief and we will come back with questions, a suggested approach, and
              next steps — at no cost.
            </p>
          </Reveal>
          <Reveal className="cta-actions">
            <Link to="/contact" className="btn btn-gold">
              Request a Quote
              <ArrowIcon />
            </Link>
            <Link to="/" className="btn btn-ghost-light">Back to Home</Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default About;
